import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaRedo } from 'react-icons/fa';
import PostCard from '../components/PostCard';
import { postsAPI } from '../services/api';
import AppBar from '../components/AppBar';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const FeedScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastPostId, setLastPostId] = useState(null);
  const [feedType, setFeedType] = useState('foryou');
  const observerRef = useRef();
  const lastPostRef = useRef();

  const fetchPosts = async (lastId = null, refresh = false) => {
    try {
      console.log('🔄 Fetching posts - Last Post ID:', lastId, 'Refresh:', refresh, 'Type:', feedType);
      
      // Get user coordinates for nearby feed
      let coordinates = null;
      if (feedType === 'nearby') {
        coordinates = await getUserLocation();
        console.log('📍 User coordinates for nearby feed:', coordinates);
      }
      
      const response = await postsAPI.getPosts(1, 20, feedType, lastId, coordinates);
      console.log('📡 API Response for lastId', lastId, ':', response);
      
      const newPosts = Array.isArray(response) ? response : (response.posts || []);
      console.log('📝 Processed posts for lastId', lastId, ':', newPosts);
      console.log('📊 Posts count:', newPosts.length);
      console.log('📋 Full API response structure:', response);
      
      // Check if API provides pagination info
      const totalPosts = response.total || response.total_posts || null;
      const hasMoreFromAPI = response.hasMore || response.has_more || null;
      
      console.log('📊 Pagination info from API:', { totalPosts, hasMoreFromAPI });
      
      if (refresh) {
        setPosts(newPosts);
        setLastPostId(null);
        console.log('🔄 Refreshed posts, total:', newPosts.length);
      } else {
        setPosts(prev => {
          const updatedPosts = [...prev, ...newPosts];
          console.log('📈 Added posts, new total:', updatedPosts.length);
          return updatedPosts;
        });
      }
      
      // Update last post ID for next request
      if (newPosts.length > 0) {
        const lastPost = newPosts[newPosts.length - 1];
        setLastPostId(lastPost.id);
        console.log('📌 Updated lastPostId to:', lastPost.id);
      }
      
      // Determine if there are more posts
      let hasMorePosts = false;
      if (hasMoreFromAPI !== null) {
        hasMorePosts = hasMoreFromAPI;
        console.log('🔍 Using API hasMore flag:', hasMorePosts);
      } else {
        hasMorePosts = newPosts.length === 20;
        console.log('🔍 Using fallback calculation:', hasMorePosts, '(expected 20, got', newPosts.length, ')');
      }
      
      setHasMore(hasMorePosts);
    } catch (error) {
      console.error('❌ Error fetching posts:', error);
      
      // Handle location-specific errors
      if (feedType === 'nearby' && error.message.includes('location')) {
        toast.error(error.message);
      } else {
        toast.error('Failed to load posts');
      }
    }
  };

  useEffect(() => {
    fetchPosts(1, true);
    setLoading(false);
  }, [feedType]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPosts(1, true);
    setRefreshing(false);
    toast.success('Feed refreshed!');
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes cache
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          console.log('📍 Current location obtained:', coordinates);
          resolve(coordinates);
        },
        (error) => {
          console.error('📍 Error getting location:', error);
          let errorMessage = 'Failed to get your location.';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
              break;
          }
          
          reject(new Error(errorMessage));
        },
        options
      );
    });
  };

  const handleFeedTypeChange = (newType) => {
    // If loops is selected, navigate to the Loops page
    if (newType === 'loops') {
      navigate('/loops');
      return;
    }
    
    // Check if we can get location for nearby feed
    if (newType === 'nearby') {
      if (!navigator.geolocation) {
        toast.error('Geolocation is not supported by this browser.');
        return;
      }
    }
    
    setFeedType(newType);
    setPosts([]);
    setLastPostId(null);
    setHasMore(true);
    setLoading(true);
  };

  const handleLoadMore = useCallback(async () => {
    console.log('🔄 handleLoadMore called - loadingMore:', loadingMore, 'hasMore:', hasMore, 'lastPostId:', lastPostId);
    if (loadingMore || !hasMore) {
      console.log('⏸️ Skipping load more - loadingMore:', loadingMore, 'hasMore:', hasMore);
      return;
    }
    
    console.log('🚀 Starting to load more posts after lastPostId:', lastPostId);
    setLoadingMore(true);
    await fetchPosts(lastPostId, false);
    setLoadingMore(false);
    console.log('✅ Finished loading more posts');
  }, [loadingMore, hasMore, lastPostId, feedType]);

  // Intersection Observer for infinite scroll
  const lastPostElementRef = useCallback(node => {
    if (loadingMore) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        handleLoadMore();
      }
    }, {
      rootMargin: '100px', // Start loading when 100px away from the last post
      threshold: 0.1
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, handleLoadMore, feedType]);

  const handlePostUpdate = (updatedPost) => {
    setPosts(prev => 
      prev.map(post => 
        post.id === updatedPost.id ? updatedPost : post
      )
    );
  };

  const handlePostDelete = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-weloop-dark">
        <AppBar />
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center py-20 text-gray-500">
            <div className="flex items-center gap-3 text-base">
              <FaSpinner className="animate-spin" />
              Loading posts...
            </div>
          </div>
        </div>
      </div>
    );
  }

  console.log('🎨 Rendering FeedScreen with posts:', posts);
  console.log('🎨 Posts length in render:', posts.length);

  return (
    <div className="min-h-screen bg-weloop-dark text-white">
      <AppBar />
      
      {/* Header */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-weloop-gray-lighter">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Feed
          </h1>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 text-weloop-green hover:bg-weloop-gray-lighter rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FaRedo className={`${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Feed Type Filter */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'foryou', label: 'For You' },
            { key: 'nearby', label: 'Nearby' },
            { key: 'friends', label: 'Friends' },
            { key: 'loops', label: 'Loops' }
          ].map((type) => (
            <button
              key={type.key}
              onClick={() => handleFeedTypeChange(type.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                feedType === type.key
                  ? 'bg-weloop-green text-white'
                  : 'bg-weloop-gray-lighter text-gray-300 hover:bg-weloop-gray hover:text-white'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <div className="text-lg font-semibold text-white mb-2">
              No posts yet
            </div>
            <div className="text-base text-gray-400">
              Follow some users to see their posts here!
            </div>
          </div>
        ) : (
          <>
            {/* Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-1">
              {posts.map((post, index) => {
                console.log('🎨 Rendering post:', post);
                const isLastPost = index === posts.length - 1;
                return (
                  <div 
                    key={post.id} 
                    className="break-inside-avoid"
                    ref={isLastPost ? lastPostElementRef : null}
                  >
                    <PostCard
                      post={post}
                      onPostUpdate={handlePostUpdate}
                      onPostDelete={handlePostDelete}
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Loading indicator for infinite scroll */}
            {loadingMore && (
              <div className="mt-8 flex justify-center">
                <div className="flex items-center gap-3 text-weloop-green">
                  <FaSpinner className="animate-spin" />
                  <span>Loading more posts...</span>
                </div>
              </div>
            )}
            
            {/* Debug info */}
            <div className="mt-4 text-center text-xs text-gray-500">
              Loaded {posts.length} posts
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedScreen; 